import { type ReactNode } from 'react';

// Shared layout for Login / Signup / Accept Invite. Mirrors MyClinicOS's
// AuthLayout: branding panel on the left, auth form on the right. Collapses
// to a single column under 960px, with a compact logo header on top.

type Props = {
  /** Heading shown above the form ("Welcome back", "Start your 30-day trial"). */
  heading: string;
  /** Supporting copy under the heading. */
  subheading?: string;
  /** Form / children rendered in the right column. */
  children: ReactNode;
};

const SPLIT_BREAKPOINT = 960;

export default function AuthLayout({ heading, subheading, children }: Props) {
  return (
    <div style={shell}>
      {/* Left — branding (Litigate navy + gold accents). Hidden under
          ${SPLIT_BREAKPOINT}px so the form column gets the full width. */}
      <aside style={brandPanel} className="auth-brand-panel">
        <div style={brandTop}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--color-accent)' }}>
            LITIGATE
          </div>
          <div style={{ fontSize: 12, color: 'rgba(232,236,242,0.65)', marginTop: 6, letterSpacing: '0.02em' }}>
            The practice management platform for advocates.
          </div>
        </div>

        <div style={brandMiddle}>
          <blockquote style={brandQuote}>
            From brief to fee note — your matters, your time, your files, your deadlines.
            All in one smart platform that thinks like advocates do.
          </blockquote>
          <div style={brandStats}>
            <div>
              <div style={brandStatValue}>Court-aware</div>
              <div style={brandStatLabel}>Every rule, embedded</div>
            </div>
            <div>
              <div style={brandStatValue}>Audit-proof</div>
              <div style={brandStatLabel}>Compliance built in</div>
            </div>
            <div>
              <div style={brandStatValue}>AI assistant</div>
              <div style={brandStatLabel}>Trained on your practice</div>
            </div>
          </div>
        </div>

        <div style={brandFooter}>© {new Date().getFullYear()} Litigate · Built for advocates</div>
      </aside>

      {/* Right — form column. */}
      <main style={formPanel}>
        <div style={formInner}>
          {/* Mobile / narrow logo bar — shown when the brand panel is hidden. */}
          <div className="auth-mobile-logo" style={mobileLogo}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-accent)', letterSpacing: '-0.01em' }}>LITIGATE</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>The practice management platform for advocates.</div>
            </div>
          </div>

          <h1 style={{ marginBottom: 6, fontSize: 26 }}>{heading}</h1>
          {subheading && (
            <p className="muted" style={{ fontSize: 14, marginBottom: 22, lineHeight: 1.5 }}>{subheading}</p>
          )}
          {children}
        </div>
      </main>

      {/* Inline @media — Litigate's CSS doesn't have a build-time breakpoint
          system, so this keeps the layout responsive without touching globals. */}
      <style>{`
        @media (max-width: ${SPLIT_BREAKPOINT - 1}px) {
          .auth-brand-panel { display: none !important; }
          .auth-mobile-logo { display: flex !important; }
        }
        @media (min-width: ${SPLIT_BREAKPOINT}px) {
          .auth-mobile-logo { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const shell: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  background: 'var(--color-bg)',
};

const brandPanel: React.CSSProperties = {
  width: '46%',
  maxWidth: 640,
  position: 'relative',
  padding: '48px 56px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: 'linear-gradient(155deg, #0F1A2E 0%, #1E2B45 55%, #2A3A58 100%)',
  borderRight: '1px solid var(--color-border)',
  color: 'var(--color-text)',
};

const brandTop: React.CSSProperties = { position: 'relative', zIndex: 1 };

const brandMiddle: React.CSSProperties = { position: 'relative', zIndex: 1 };

const brandFooter: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(232,236,242,0.45)',
  position: 'relative',
  zIndex: 1,
};

const brandQuote: React.CSSProperties = {
  fontSize: 22,
  lineHeight: 1.45,
  fontWeight: 500,
  color: 'rgba(232,236,242,0.92)',
  borderLeft: '3px solid var(--color-accent)',
  paddingLeft: 18,
  margin: 0,
};

const brandStats: React.CSSProperties = {
  display: 'flex',
  gap: 28,
  marginTop: 32,
  paddingTop: 24,
  borderTop: '1px solid rgba(232,236,242,0.10)',
  flexWrap: 'wrap',
};

const brandStatValue: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--color-accent)',
  letterSpacing: '-0.01em',
};

const brandStatLabel: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(232,236,242,0.55)',
  marginTop: 4,
  letterSpacing: '0.02em',
};

const formPanel: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 32px',
  background: 'var(--color-bg)',
};

const formInner: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
};

const mobileLogo: React.CSSProperties = {
  display: 'none',
  alignItems: 'center',
  gap: 12,
  marginBottom: 28,
};
